import { zodResolver } from "@hookform/resolvers/zod";

import { Loader2, Trash } from "lucide-react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  type LectureFormDto,
  createLectureDtoSchema,
  type SessionDto,
  type SpeakerDto,
  type TagDto,
} from "slde-api/dto-schemas";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { DateFormField } from "@/components/date.form-field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import { useResources } from "@/providers/resources.provider";
import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";

interface LectureFormProps {
  defaultValues?: LectureFormDto;
  onSubmit: (values: LectureFormDto) => void;

  disabled?: boolean;
  loading?: boolean;
}
export function LectureForm({
  onSubmit: onSubmitProps,
  defaultValues,
  disabled,
  loading,
}: LectureFormProps) {
  const {
    rooms,
    tags,
    speakers,
    sessions,
    createSpeaker,
    isCreatingSpeaker,
    deleteSpeaker,
    isDeletingSpeaker,
    createTag,
    isCreatingTag,
    deleteTag,
    isDeletingTag,
    createSession,
    isCreatingSession,
    deleteSession,
    isDeletingSession,
  } = useResources();

  console.log(defaultValues);
  const [speakerInput, setSpeakerInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [sessionInput, setSessionInput] = useState("");
  const [withSession, setWithSession] = useState(!!defaultValues?.session);

  const anchor = useComboboxAnchor();
  const tagsAnchor = useComboboxAnchor();

  const form = useForm<LectureFormDto>({
    resolver: zodResolver(createLectureDtoSchema),
    defaultValues: defaultValues
      ? {
          ...defaultValues,
          startTime: new Date(defaultValues.startTime).toLocaleTimeString("pl"),
          endTime: new Date(defaultValues.endTime).toLocaleTimeString("pl"),
          speakerIds: defaultValues.speakers?.map(
            (speaker: SpeakerDto) => speaker.id,
          ),
          tagIds: defaultValues.categories?.map((cat: TagDto) => cat.id),
          roomId: defaultValues.room?.id,
          sessionId: defaultValues.session?.id ?? null,
        }
      : {},
  });

  function onSubmit(values: LectureFormDto) {
    const startTimeDate = new Date(values.dayDate);
    const startTimeArr = values.startTime.split(":");
    startTimeDate.setHours(startTimeArr[0]);
    startTimeDate.setMinutes(startTimeArr[1]);

    const endTimeDate = new Date(values.dayDate);
    const endTimeArr = values.endTime.split(":");
    endTimeDate.setHours(endTimeArr[0]);
    endTimeDate.setMinutes(endTimeArr[1]);
    onSubmitProps({
      ...values,
      startTime: startTimeDate.toISOString(),
      endTime: endTimeDate.toISOString(),
    });
  }

  return (
    <div className="flex gap-4">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, (e) => console.log(e))}
          className="space-y-4 w-full"
        >
          <FormField
            control={form.control}
            name="title"
            render={({ field: { value, ...field } }) => (
              <FormItem>
                <FormLabel>Tytuł</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Nowoczesne metody..."
                    value={value ?? ""}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field: { value, ...field } }) => (
              <FormItem>
                <FormLabel>Opis</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Przegląd najnowszych technik..."
                    value={value ?? ""}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DateFormField control={form.control} name="dayDate" />

          <FormField
            control={form.control}
            name="startTime"
            render={({ field: { value, ...field } }) => (
              <FormItem>
                <FormLabel>Godzina rozpoczęcia</FormLabel>
                <FormControl>
                  <Input
                    type="time"
                    step="1"
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                    value={value ?? ""}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endTime"
            render={({ field: { value, ...field } }) => (
              <FormItem>
                <FormLabel>Godzina zakończenia</FormLabel>
                <FormControl>
                  <Input
                    type="time"
                    step="1"
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                    value={value ?? ""}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="order"
            render={({ field: { value, onChange, ...field } }) => (
              <FormItem>
                <FormLabel>Kolejność</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    value={value ?? ""}
                    onChange={(e) =>
                      onChange(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex items-center gap-2">
            <Checkbox
              checked={withSession}
              onCheckedChange={(val) => setWithSession(!!val)}
            />
            <FormLabel>Wykład jest częścią sesji</FormLabel>
          </div>

          {withSession && (
            <FormField
              control={form.control}
              name="sessionId"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Sesja</FormLabel>
                  <FormControl>
                    <Combobox
                      items={sessions}
                      value={value}
                      onValueChange={onChange}
                      autoHighlight
                      itemToStringLabel={(item: any) =>
                        typeof item === "object" && item !== null
                          ? item.title ?? ""
                          : sessions.find((s) => s.id === item)?.title ?? ""
                      }
                    >
                      <ComboboxInput
                        showClear
                        className="w-full"
                        onChange={(e) => setSessionInput(e.target.value)}
                      />
                      <ComboboxContent>
                        <ComboboxEmpty>
                          <div className="flex flex-col items-center gap-2">
                            <span>Brak wyników</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={isCreatingSession || !sessionInput}
                              onClick={() =>
                                createSession({ title: sessionInput })
                              }
                            >
                              {isCreatingSession && (
                                <Loader2 className="animate-spin" />
                              )}
                              Dodaj &ldquo;{sessionInput}&rdquo;
                            </Button>
                          </div>
                        </ComboboxEmpty>
                        <ComboboxList>
                          {(session: SessionDto) => (
                            <ComboboxItem key={session.id} value={session.id}>
                              <div className="flex items-center justify-between w-full">
                                <span>{session.title}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="link"
                                  disabled={isDeletingSession}
                                  onClick={() =>
                                    deleteSession({ id: session.id })
                                  }
                                >
                                  {isDeletingSession && (
                                    <Loader2 className="animate-spin" />
                                  )}
                                  <Trash />
                                </Button>
                              </div>
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="speakerIds"
            render={({ field: { value, onChange } }) => {
              return (
                <FormItem>
                  <FormLabel>Prelegenci</FormLabel>
                  <FormControl>
                    <Combobox
                      items={speakers}
                      multiple
                      value={value ?? []}
                      onValueChange={onChange}
                      autoHighlight
                    >
                      <ComboboxChips ref={anchor} className="w-full">
                        <ComboboxValue>
                          {(values) => (
                            <React.Fragment>
                              {speakers
                                .filter((speaker) =>
                                  values?.includes(speaker.id),
                                )
                                .map((speaker: SpeakerDto) => (
                                  <ComboboxChip key={speaker.id}>
                                    {speaker.fullName}
                                  </ComboboxChip>
                                ))}
                              <ComboboxChipsInput
                                onChange={(e) =>
                                  setSpeakerInput(e.target.value)
                                }
                              />
                            </React.Fragment>
                          )}
                        </ComboboxValue>
                      </ComboboxChips>
                      <ComboboxContent anchor={anchor}>
                        <ComboboxEmpty>
                          <div className="flex flex-col items-center gap-2">
                            <span>Brak wyników</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={isCreatingSpeaker || !speakerInput}
                              onClick={() =>
                                createSpeaker({ fullName: speakerInput })
                              }
                            >
                              {isCreatingSpeaker && (
                                <Loader2 className="animate-spin" />
                              )}
                              Dodaj &ldquo;{speakerInput}&rdquo;
                            </Button>
                          </div>
                        </ComboboxEmpty>
                        <ComboboxList>
                          {(speaker: SpeakerDto) => (
                            <ComboboxItem key={speaker.id} value={speaker.id}>
                              <div className="flex items-center justify-between w-full">
                                <span> {speaker.fullName}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="link"
                                  disabled={isDeletingSpeaker}
                                  onClick={(e) => {
                                    deleteSpeaker({ id: speaker.id });
                                  }}
                                >
                                  {isDeletingSpeaker && (
                                    <Loader2 className="animate-spin" />
                                  )}
                                  <Trash />
                                </Button>
                              </div>
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="tagIds"
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormLabel>Kategorie</FormLabel>
                <FormControl>
                  <Combobox
                    items={tags}
                    multiple
                    value={value ?? []}
                    onValueChange={onChange}
                    autoHighlight
                  >
                    <ComboboxChips ref={tagsAnchor} className="w-full">
                      <ComboboxValue>
                        {(values) => (
                          <React.Fragment>
                            {tags
                              .filter((tag) => values?.includes(tag.id))
                              .map((tag: TagDto) => (
                                <ComboboxChip key={tag.id}>
                                  {tag.name}
                                </ComboboxChip>
                              ))}
                            <ComboboxChipsInput
                              onChange={(e) => setTagInput(e.target.value)}
                            />
                          </React.Fragment>
                        )}
                      </ComboboxValue>
                    </ComboboxChips>
                    <ComboboxContent anchor={tagsAnchor}>
                      <ComboboxEmpty>
                        <div className="flex flex-col items-center gap-2">
                          <span>Brak wyników</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={isCreatingTag || !tagInput}
                            onClick={() => createTag({ name: tagInput })}
                          >
                            {isCreatingTag && (
                              <Loader2 className="animate-spin" />
                            )}
                            Dodaj &ldquo;{tagInput}&rdquo;
                          </Button>
                        </div>
                      </ComboboxEmpty>
                      <ComboboxList>
                        {(tag: TagDto) => (
                          <ComboboxItem key={tag.id} value={tag.id}>
                            <div className="flex items-center justify-between w-full">
                              <span>{tag.name}</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="link"
                                disabled={isDeletingTag}
                                onClick={(e) => {
                                  deleteTag({ id: tag.id });
                                }}
                              >
                                {isDeletingTag && (
                                  <Loader2 className="animate-spin" />
                                )}
                                <Trash />
                              </Button>
                            </div>
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="roomId"
            render={({ field: { value, onChange } }) => {
              return (
                <FormItem>
                  <FormLabel>Sala</FormLabel>
                  <FormControl>
                    <Select
                      value={value?.toString()}
                      onValueChange={(val) => {
                        const num = Number(val);
                        return onChange(num >= 0 ? num : null);
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {rooms.map((room) => (
                            <SelectItem
                              key={room.id}
                              value={room.id.toString()}
                            >
                              {room.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <Button type="submit" disabled={disabled || loading}>
            {!!defaultValues ? "Edytuj" : "Dodaj"}
            {loading && <Loader2 className="animate-spin" />}
          </Button>
        </form>
      </Form>
    </div>
  );
}
